/**
 * Safe storage utility using localForage for cross-browser compatibility
 * Automatically handles fallbacks: IndexedDB → WebSQL → localStorage → in-memory
 */

import localforage from "localforage";

// Configure localForage for better compatibility
localforage.config({
  name: "MarketBuzzCRM",
  version: 1.0,
  storeName: "app_data", // Should be alphanumeric, with underscores.
  description: "MarketBuzz CRM application data storage"
});

// Convenience functions with error handling
export const safeGetItem = async (key: string): Promise<string | null> => {
  try {
    const value = await localforage.getItem<string>(key);
    return value;
  } catch (error) {
    console.warn(`Failed to get item "${key}" from storage:`, error);
    return null;
  }
};

export const safeSetItem = async (
  key: string,
  value: string
): Promise<void> => {
  try {
    await localforage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set item "${key}" in storage:`, error);
  }
};

export const safeRemoveItem = async (key: string): Promise<void> => {
  try {
    await localforage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove item "${key}" from storage:`, error);
  }
};

export const safeClear = async (): Promise<void> => {
  try {
    await localforage.clear();
  } catch (error) {
    console.warn("Failed to clear storage:", error);
  }
};

// Synchronous versions for backward compatibility (less reliable but won't break existing code)
export const safeGetItemSync = (key: string): string | null => {
  try {
    // Try localStorage first for synchronous access
    if (typeof localStorage !== "undefined" && localStorage !== null) {
      return localStorage.getItem(key);
    }
    return null;
  } catch {
    return null;
  }
};

export const safeSetItemSync = (key: string, value: string): void => {
  try {
    // Try localStorage first for synchronous access
    if (typeof localStorage !== "undefined" && localStorage !== null) {
      localStorage.setItem(key, value);
    }
    // Also save to localForage asynchronously for persistence
    safeSetItem(key, value).catch(() => {
      // Silent fail
    });
  } catch {
    // Silent fail, but still try async save
    safeSetItem(key, value).catch(() => {
      // Silent fail
    });
  }
};

export const safeRemoveItemSync = (key: string): void => {
  try {
    if (typeof localStorage !== "undefined" && localStorage !== null) {
      localStorage.removeItem(key);
    }
  } catch {
    // Silent fail if localStorage is unavailable
  }
};

// Check if any storage is available
export const isLocalStorageAvailable = (): boolean => {
  try {
    if (typeof localStorage === "undefined" || localStorage === null) {
      return false;
    }
    const test = "__localStorage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

export const safeLocalStorageKeys = (): string[] => {
  try {
    if (typeof localStorage === "undefined" || localStorage === null) {
      return [];
    }
    return Object.keys(localStorage);
  } catch {
    return [];
  }
};

// Export localforage instance for advanced usage
export const storage = localforage;

// Safe sessionStorage utilities
export const safeSessionGetItem = (key: string): string | null => {
  try {
    if (typeof sessionStorage === "undefined" || sessionStorage === null) {
      return null;
    }
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

export const safeSessionSetItem = (key: string, value: string): void => {
  try {
    if (typeof sessionStorage === "undefined" || sessionStorage === null) {
      return;
    }
    sessionStorage.setItem(key, value);
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

export const safeSessionRemoveItem = (key: string): void => {
  try {
    if (typeof sessionStorage === "undefined" || sessionStorage === null) {
      return;
    }
    sessionStorage.removeItem(key);
  } catch {
    // Silently fail if sessionStorage is not available
  }
};
